import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import Navbar from '../components/shared/Navbar';
import DashboardSidebar from '../components/shared/DashboardSidebar';
import Footer from '../components/shared/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Landmark, DollarSign, FileText, Clock, CheckCircle, ArrowRight,
  TrendingUp, Shield, CreditCard, Loader2, AlertCircle
} from 'lucide-react';

const TradeFinancePage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [products, setProducts] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/finance/products`);
      setProducts(response.data.products);

      if (isAuthenticated) {
        const token = localStorage.getItem('atff_token');
        const appsResponse = await axios.get(`${API}/finance/applications`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          // withCredentials removed for CORS compatibility
        });
        setApplications(appsResponse.data.applications || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const financeIcons = {
    export_credit: CreditCard,
    invoice_financing: FileText,
    working_capital: DollarSign,
    supply_chain_finance: TrendingUp,
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'submitted':
        return <Badge className="bg-blue-100 text-blue-700"><Clock className="w-3 h-3 mr-1" /> Under Review</Badge>;
      case 'approved':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" /> Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700"><AlertCircle className="w-3 h-3 mr-1" /> Rejected</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className={isAuthenticated ? 'flex' : ''}>
        {/* Sidebar for authenticated users */}
        {isAuthenticated && <DashboardSidebar />}

        <div className={isAuthenticated ? 'flex-1' : ''}>
          {/* Hero */}
          <section className={`bg-charcoal text-white py-12 ${isAuthenticated ? 'mx-6 mt-6 rounded-lg' : ''}`} data-testid="finance-hero">
            <div className="container-main">
              <Badge className="bg-oceanBlue text-white mb-4">Trade Finance</Badge>
              <h1 className="text-3xl md:text-4xl font-bold mb-4 font-poppins">
                Access Trade Finance
              </h1>
              <p className="text-gray-300 max-w-xl">
                Get financing for your cross-border trade. Export credit, invoice financing, 
                working capital, and supply chain finance from partner institutions.
              </p>
            </div>
          </section>

          <div className={isAuthenticated ? 'p-6' : 'container-main py-12'}>
            {/* Finance Products */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-charcoal mb-6 font-poppins">Finance Products</h2>
              <div className="grid md:grid-cols-2 gap-6">
                {loading ? (
                  <div className="col-span-2 flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-continental" />
                  </div>
                ) : (
                  products.map((product) => {
                    const Icon = financeIcons[product.id] || Landmark;
                    return (
                      <Card key={product.id} className="card-interactive" data-testid={`finance-product-${product.id}`}>
                        <CardContent className="p-6">
                          <div className="flex items-start gap-4">
                            <div className="w-14 h-14 bg-oceanBlue/10 rounded-xl flex items-center justify-center flex-shrink-0">
                              <Icon className="w-7 h-7 text-oceanBlue" />
                            </div>
                            <div className="flex-1">
                              <h3 className="font-semibold text-charcoal text-lg mb-1">{product.name}</h3>
                              <p className="text-gray-500 text-sm mb-4">{product.description}</p>
                              
                              <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                                <div>
                                  <p className="text-gray-500">Max Amount</p>
                              <p className="font-semibold text-charcoal">
                                ${product.max_amount.toLocaleString()}
                              </p>
                            </div>
                            <div>
                              <p className="text-gray-500">Interest Rate</p>
                              <p className="font-semibold text-charcoal">{product.interest_rate}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Term</p>
                              <p className="font-semibold text-charcoal">{product.term}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Currency</p>
                              <p className="font-semibold text-charcoal">{product.currency}</p>
                            </div>
                          </div>

                          <Button 
                            onClick={() => {
                              if (!isAuthenticated) {
                                toast.error('Please login to apply');
                                navigate('/login');
                                return;
                              }
                              toast.info('Complete an order first to apply for financing');
                              navigate('/marketplace');
                            }}
                            className="w-full btn-primary"
                          >
                            Apply Now
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        </div>

        {/* My Applications */}
        {isAuthenticated && (
          <div>
            <h2 className="text-2xl font-bold text-charcoal mb-6 font-poppins">My Applications</h2>
            <Card>
              <CardContent className="p-6">
                {applications.length === 0 ? (
                  <div className="text-center py-8">
                    <Landmark className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No finance applications yet</p>
                    <p className="text-sm text-gray-400 mt-1">
                      Complete a trade order to apply for financing
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {applications.map((app) => (
                      <div key={app.application_id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium text-charcoal capitalize">
                            {app.finance_type.replace('_', ' ')}
                          </p>
                          <p className="text-sm text-gray-500">
                            ${app.amount_requested.toLocaleString()} · {app.repayment_period_months} months
                          </p>
                        </div>
                        {getStatusBadge(app.status)}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Info Section */}
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          <Card>
            <CardContent className="p-6 text-center">
              <Shield className="w-10 h-10 text-continental mx-auto mb-3" />
              <h3 className="font-semibold text-charcoal mb-2">Secure & Trusted</h3>
              <p className="text-sm text-gray-500">Partner with Afreximbank and leading African financial institutions</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6 text-center">
              <Clock className="w-10 h-10 text-oceanBlue mx-auto mb-3" />
              <h3 className="font-semibold text-charcoal mb-2">Fast Processing</h3>
              <p className="text-sm text-gray-500">Applications processed within 2-5 business days</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6 text-center">
              <TrendingUp className="w-10 h-10 text-tradeGold mx-auto mb-3" />
              <h3 className="font-semibold text-charcoal mb-2">Competitive Rates</h3>
              <p className="text-sm text-gray-500">Special rates for verified AfCFTA traders</p>
            </CardContent>
          </Card>
        </div>
          </div>
        </div>
      </div>

      {!isAuthenticated && <Footer />}
    </div>
  );
};

export default TradeFinancePage;
